import { EventsConfig, RangeHorizon } from '@chart';
import { ChartV2Insight } from '@chartv2';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useChartInsights } from '@terminal/views/turbo-chart/hooks/use-chart-insights/useChartInsights';
import type { TerminalPaneData } from '@terminal/views/turbo-chart/utils/chart-utils/types';
import { useMemo } from 'react';

import {
  buildEntityPaneIndex,
  filterByEventsConfig,
  toChartV2Insights,
} from './useChartV2Insights.utils';

export interface UseChartV2InsightsOptions {
  chartPanes: TerminalPaneData[];
  eventsConfig: EventsConfig;
  chartHorizon?: RangeHorizon;
  small?: boolean;
  source?: PosthogTrackingSource;
}

/**
 * Bridge hook: fetches the real insights via the terminal's existing
 * `useChartInsights` pipeline and shapes them for `<ChartV2 insights={…} />`.
 * Attaches each insight to the pane that owns its entity so insights follow
 * the series when the user moves it between panes.
 */
export const useChartV2Insights = ({
  chartPanes,
  eventsConfig,
  small,
  source,
  chartHorizon,
}: UseChartV2InsightsOptions): ChartV2Insight[] => {
  const chartData = useMemo(
    () => chartPanes.flatMap(pane => pane.chartAssetData),
    [chartPanes]
  );

  const { insights } = useChartInsights({
    chartData,
    eventsConfig,
    chartHorizon,
  });

  return useMemo(() => {
    const filtered = filterByEventsConfig(insights, eventsConfig);
    const entityPaneIndex = buildEntityPaneIndex(chartPanes);

    return toChartV2Insights(filtered, entityPaneIndex, { small, source });
  }, [insights, eventsConfig, chartPanes, small, source]);
};
